import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Link as MuiLink,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { adminApi } from '../../services/adminApi';

/**
 * Admin per-user activity timeline (#211).
 *
 * Read-only view of a single user's safe profile plus recent activity streams
 * (searches, outbound clicks, saved searches, watchlist, alerts). This is admin
 * troubleshooting, not an advertiser export — no behavioral profile is built
 * and nothing leaves the admin surface (§10.5–§10.7). Auth is enforced upstream
 * by AdminLayout.
 */
export function UserDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const query = useQuery({
    queryKey: ['admin', 'user', id],
    queryFn: () => adminApi.getUser(id),
  });

  return (
    <Box>
      <MuiLink component={RouterLink} to="/admin/users" sx={{ fontSize: '0.875rem' }}>
        ← Back to users
      </MuiLink>

      {query.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress aria-label="Loading user activity" />
        </Box>
      )}

      {query.isError && (
        <Alert severity="error" role="alert" sx={{ mt: 2 }}>
          Failed to load user: {(query.error as Error).message}
        </Alert>
      )}

      {query.isSuccess && (
        <>
          <Typography variant="h1" sx={{ fontSize: '1.6rem', mt: 1, mb: 0.5 }}>
            {query.data.user.email}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
            <Chip label={query.data.user.role} size="small" />
            <Chip
              label={query.data.user.isActive ? 'Active' : 'Suspended'}
              color={query.data.user.isActive ? 'success' : 'default'}
              size="small"
            />
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: 2,
            }}
          >
            <ActivityPanel
              title="Recent searches"
              empty="No searches recorded."
              items={query.data.recentSearches.map((s) => ({
                key: s.id,
                primary: s.query || s.caliber || '(filters only)',
                secondary: s.caliber ?? undefined,
              }))}
            />
            <ActivityPanel
              title="Recent outbound clicks"
              empty="No outbound clicks."
              items={query.data.recentClicks.map((c) => ({
                key: c.id,
                primary: c.retailerName ?? c.retailerId ?? 'Unknown retailer',
                secondary: c.sourceUrl,
              }))}
            />
            <ActivityPanel
              title="Saved searches"
              empty="No saved searches."
              items={query.data.savedSearches.map((s) => ({
                key: s.id,
                primary: s.name,
                secondary: s.query ?? undefined,
              }))}
            />
            <ActivityPanel
              title="Alerts"
              empty="No alerts."
              items={query.data.alerts.map((a) => ({
                key: a.id,
                primary: a.name,
                secondary: a.isActive ? 'active' : 'inactive',
              }))}
            />
          </Box>
        </>
      )}
    </Box>
  );
}

/** One activity stream panel — a titled list with an empty state. */
function ActivityPanel({
  title,
  items,
  empty,
}: {
  title: string;
  items: Array<{ key: string; primary: string; secondary?: string }>;
  empty: string;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h2" component="h2" sx={{ fontSize: '1rem', fontWeight: 700, mb: 1 }}>
        {title}
      </Typography>
      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {empty}
        </Typography>
      ) : (
        <List dense disablePadding>
          {items.map((it) => (
            <ListItem key={it.key} disableGutters sx={{ py: 0.25 }}>
              <ListItemText
                primary={it.primary}
                secondary={it.secondary}
                primaryTypographyProps={{ variant: 'body2' }}
                secondaryTypographyProps={{
                  variant: 'caption',
                  sx: { wordBreak: 'break-all' },
                }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Paper>
  );
}

export default UserDetailPage;
